/**
 * Traducción de errores técnicos (PostgreSQL / PostgREST / Supabase Storage /
 * red) a mensajes de negocio claros y accionables — Ticket Mantis #490.
 *
 * Regla: la UI NUNCA debe mostrar el texto crudo del motor (por ejemplo
 * "new row violates row-level security policy"). El detalle técnico se
 * conserva en `console.error` para diagnóstico; al usuario se le muestra
 * el string que devuelve `mapSupabaseError()`.
 *
 * El helper es puro (sin efectos): el logueo del error crudo lo hace quien
 * lo invoca, típicamente en el componente que muestra el Alert.
 */

/** Forma laxa de los errores que devuelven Supabase / PostgREST / Storage. */
export interface ErrorLike {
  code?: string | number;
  message?: string;
  details?: string | null;
  hint?: string | null;
  error_description?: string;
  status?: number;
  statusCode?: number | string;
  name?: string;
}

/** Mensaje por defecto cuando no reconocemos el error. */
const FALLBACK_DEFAULT =
  'Ocurrió un error inesperado. Volvé a intentar en unos minutos; si persiste, avisá al administrador.';

/**
 * Cubre las dos causas posibles de un rechazo por RLS de cara al usuario:
 * falta de permiso real y sesión vencida. Es accionable sin exponer la RLS.
 */
const MSG_RLS =
  'No pudimos completar la operación: tu usuario no tiene permiso para hacerla o tu sesión expiró. ' +
  'Cerrá sesión, volvé a entrar e intentá de nuevo. Si el problema persiste, avisá al administrador.';

const MSG_SESION =
  'Tu sesión expiró. Cerrá sesión, volvé a entrar e intentá de nuevo.';

const MSG_RED =
  'No pudimos conectar con el servidor. Revisá tu conexión a internet e intentá de nuevo.';

/** Normaliza el error de entrada a un objeto con message/code accesibles. */
function normalizar(err: unknown): ErrorLike {
  if (err == null) return {};
  if (typeof err === 'string') return { message: err };
  if (typeof err === 'object') return err as ErrorLike;
  return { message: String(err) };
}

/** Quita anotaciones internas del tipo "(decision #023)" de un mensaje nuestro. */
function limpiarMensajePropio(msg: string): string {
  return msg.replace(/\s*\((?:decisi[oó]n|decision)\s*#\d+\)\s*/gi, ' ').trim();
}

/**
 * Traduce un error de Supabase/PostgREST/Storage/red a un mensaje de negocio.
 *
 * @param err      El error crudo capturado (unknown).
 * @param fallback Mensaje a mostrar si no reconocemos el error.
 * @returns        Texto claro y accionable en español, sin jerga del motor.
 */
export function mapSupabaseError(err: unknown, fallback: string = FALLBACK_DEFAULT): string {
  const e = normalizar(err);
  const code = e.code != null ? String(e.code) : '';
  const rawMsg = (e.message || e.error_description || '').toString();
  const msg = rawMsg.toLowerCase();
  const status = Number(e.status ?? e.statusCode ?? NaN);

  // ── Red / servidor caído ────────────────────────────────────────────────
  if (
    e.name === 'TypeError' ||
    msg.includes('failed to fetch') ||
    msg.includes('networkerror') ||
    msg.includes('load failed') ||
    msg.includes('fetch failed')
  ) {
    return MSG_RED;
  }

  // ── Sesión / JWT ────────────────────────────────────────────────────────
  if (
    status === 401 ||
    code === '401' ||
    code === 'PGRST301' ||
    msg.includes('jwt') ||
    msg.includes('invalid token') ||
    msg.includes('not authenticated') ||
    msg.includes('token expired')
  ) {
    return MSG_SESION;
  }

  // ── RLS (el caso central del #490) ──────────────────────────────────────
  if (code === '42501' || msg.includes('row-level security') || msg.includes('row level security')) {
    return MSG_RLS;
  }

  // ── Violaciones de integridad conocidas ─────────────────────────────────
  if (code === '23505' || msg.includes('duplicate key')) {
    return 'Ya existe un registro con esos mismos datos.';
  }
  if (code === '22003' || msg.includes('numeric field overflow')) {
    return 'Alguno de los montos ingresados es demasiado grande. Revisá el valor e intentá de nuevo.';
  }
  if (code === '23502') {
    return 'Falta completar un dato obligatorio.';
  }
  if (code === '23503') {
    return 'El registro está vinculado a otros datos, por eso no se pudo completar la operación.';
  }
  if (code === '23514') {
    return 'Alguno de los datos ingresados no cumple una regla del sistema. Revisá montos y fechas.';
  }

  // ── Mensajes propios (RAISE de plpgsql): ya están en lenguaje de negocio ──
  // Los devolvemos tal cual, solo limpiando anotaciones internas.
  if (code === 'P0001' || code === 'P0000') {
    const propio = limpiarMensajePropio(rawMsg);
    return propio || fallback;
  }

  // ── Storage: nombre de archivo repetido ─────────────────────────────────
  if (msg.includes('already exists') || msg.includes('resource already exists')) {
    return 'Ese archivo ya fue subido. Volvé a intentar.';
  }

  return fallback;
}
